// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Connection } from '@shared/commons/Connection';
import { ConnectionStates } from '@shared/utils/Connections/connection.types';
import { Resources } from '@shared/localizations/Resources';
import { TerceraLookupDropDownForm } from './TerceraLookupDropDownForm';
import { MainWindowManager } from '../../UtilsClasses/MainWindowManager';
import { MultiComboBoxEvents } from '../TerceraMultiComboBox';
import { InstrumentLookupManager } from './InstrumentLookupManager';
import { InstrumentTypes, isFuturesInsteadSpot } from '@shared/utils/Instruments/InstrumentTypes';
import { Instrument } from '@shared/commons/cache/Instrument';
import TerceraInstrumentLookupDropDownFormTemplate from '@front/templates/TerceraInstrumentLookupDropDownFormTemplate.html';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { DataCache } from '@shared/commons/DataCache';
import { InstrumentLookupComparer } from '@shared/utils/Instruments/InstrumentLookupComparer';
import { InstrumentLookupInstrumentType } from '@shared/utils/Instruments/InstrumentLookupInstrumentType';
import { InstrumentLookupQuickTreeNode } from '../QuickTree/InstrumentLookupQuickTreeNode';
import { InstrumentLookupProperties, InstrumentLookupType } from './InstrumentLookupManagerProps';
import { ScreensNames } from '../../UtilsClasses/FactoryConstants';
export class TerceraInstrumentLookupDropDownForm extends TerceraLookupDropDownForm {
    getType() {
        return ScreensNames.TerceraInstrumentLookupDropDownForm;
    }
    constructor() {
        super();
        this.types = null;
        this.exchanges = null;
        this.onConnectionStateChange = () => {
            const connectionStates = ConnectionStates;
            const connectionState = Connection.connectionState;
            if (connectionState === connectionStates.DISCONNECTED || connectionState === connectionStates.CONNECTION_LOST) {
                MainWindowManager.TerceraInstrumentLookupDropDownForm = null;
                Connection.unsubscribeOnConnectStateChange(this.onConnectionStateChange);
                this.dispose();
            }
        };
    }
    initializationComponents() {
        this.textBox = this.Controls.textBox;
        this.exchanges = this.Controls.exchanges;
        this.types = this.Controls.types;
        this.collapseAllBtn = this.Controls.collapseAllBtn;
        this.expandExchangesBtn = this.Controls.expandExchangesBtn;
        this.expandTypesBtn = this.Controls.expandTypesBtn;
        const qtr = this.Controls.quickTree;
        if (!isNullOrUndefined(qtr === null || qtr === void 0 ? void 0 : qtr.quickTree)) {
            this.quickTree = qtr;
            qtr.quickTree.rowHeigth = InstrumentLookupQuickTreeNode.ROW_HEIGHT;
        }
    }
    static expandNodes(nodes, expandNodeLevel, currentNodeLevel) {
        if (!isValidArray(nodes)) {
            return;
        }
        const len = nodes.length;
        if (!len) {
            return;
        }
        currentNodeLevel = isNullOrUndefined(currentNodeLevel) ? 0 : currentNodeLevel;
        const collapse = currentNodeLevel > expandNodeLevel;
        const nextNodeLevel = currentNodeLevel + 1;
        for (let i = 0; i < len; i++) {
            const node = nodes[i];
            if (node.childNodes.length > 0) {
                node.collapsed = collapse;
            }
            TerceraInstrumentLookupDropDownForm.expandNodes(node.childNodes, expandNodeLevel, nextNodeLevel);
        }
    }
    collapseAll() {
        const qt = this.quickTree.quickTree;
        const nodeCollection = qt.nodeCollection;
        TerceraInstrumentLookupDropDownForm.expandNodes(nodeCollection, 0, 1);
        qt.Draw(true);
    }
    clickExchange(bool) {
        let type = NodeType.InstrumentType;
        if (bool) {
            type = NodeType.Exchange;
        }
        this.expandNodesByType(type);
    }
    expandNodesByType(type) {
        const qt = this.quickTree.quickTree;
        const nodeCollection = qt.nodeCollection;
        if (!this.needShowExchanges()) // https://tp.traderevolution.com/entity/84756 (3rd issue)
         {
            type = type === NodeType.Exchange ? NodeType.InstrumentType : NodeType.InstrumentGroup;
        }
        const level = TerceraInstrumentLookupDropDownForm.findNodeTypeTreeLevel(nodeCollection, type);
        if (level === -1) {
            return;
        }
        TerceraInstrumentLookupDropDownForm.expandNodes(nodeCollection, level);
        qt.Draw(true);
    }
    static getNodeType(node) {
        if (isNullOrUndefined(node)) {
            return null;
        }
        const tag = node.tag;
        if (tag === 'exchange') {
            return NodeType.Exchange;
        }
        if (typeof tag === 'number') {
            return NodeType.InstrumentType;
        }
        if (tag === 'group') {
            return NodeType.InstrumentGroup;
        }
        if (tag instanceof Instrument) {
            return NodeType.Instrument;
        }
        return NodeType.Other;
    }
    // TODO. Optimize if necessary.
    static findNodeTypeTreeLevel(nodeArray, nodeType, currentNodeLevel) {
        if (!isValidArray(nodeArray)) {
            return -1;
        }
        currentNodeLevel = isNullOrUndefined(currentNodeLevel) ? 0 : currentNodeLevel;
        // TODO. Remove loop?
        for (let i = 0, len = nodeArray.length; i < len; i++) {
            const node = nodeArray[i];
            if (TerceraInstrumentLookupDropDownForm.getNodeType(node) === nodeType) {
                return currentNodeLevel;
            }
            const foundNodeLevel = TerceraInstrumentLookupDropDownForm.findNodeTypeTreeLevel(node.childNodes, nodeType, currentNodeLevel + 1);
            if (foundNodeLevel !== -1) {
                return foundNodeLevel;
            }
        }
        return -1;
    }
    selectFirstTopNode() {
        const qt = this.quickTree.quickTree;
        const plainArray = qt.plainArray;
        if (plainArray.length > 0) {
            qt.CheckUnCheckAll(false);
            qt.setSelectedNode(plainArray[0]);
        }
    }
    arrangeControls() {
        const exchangesCB = this.exchanges;
        const showExchanges = this.needShowExchanges();
        void exchangesCB.set('visible', showExchanges);
        const qTree = this.quickTree;
        qTree.setSizes();
    }
    needShowExchanges() {
        const exchanges = this.exchanges;
        if (isNullOrUndefined(exchanges))
            return false;
        const items = exchanges.get('items');
        return isValidArray(items);
    }
    needShowTypes() {
        const types = this.types;
        if (isNullOrUndefined(types))
            return false;
        const items = types.get('items');
        return isValidArray(items);
    }
    getInstrumentFromSelectedNode() {
        const selectedNode = this.quickTree.quickTree.selectedNode;
        const tag = !isNullOrUndefined(selectedNode) ? selectedNode.tag : null;
        return tag instanceof Instrument ? tag : null;
    }
    static createInstance() {
        return __awaiter(this, void 0, void 0, function* () {
            const lookup = new TerceraInstrumentLookupDropDownForm();
            lookup.setBounds(0, 0, 500, 550);
            void lookup.set('visible', false);
            MainWindowManager.AddControl(lookup);
            Connection.subscribeOnConnectStateChange(lookup.onConnectionStateChange);
            return yield lookup.OnCompliteWaiter;
        });
    }
    static ShowForm(parametersStruct) {
        let instanceWaiter = null;
        if (isNullOrUndefined(MainWindowManager.TerceraInstrumentLookupDropDownForm)) {
            instanceWaiter = TerceraInstrumentLookupDropDownForm.createInstance().then((instance) => {
                MainWindowManager.TerceraInstrumentLookupDropDownForm = instance;
                return instance;
            });
        }
        else {
            instanceWaiter = Promise.resolve(MainWindowManager.TerceraInstrumentLookupDropDownForm);
        }
        // TODO. Ugly. Refactor.
        // Resetting stuff.
        // These setters don't invoke fillTree() due to unsubscribing before.
        // Thus, combo boxes will be populated only once.
        instanceWaiter.then((instance) => {
            instance.unsubscribeCBValueChanged();
            instance.types.set('items', null);
            instance.exchanges.set('items', null);
            instance.currentNameFilter = instance.oldNameFilter = null;
            instance.showForm(parametersStruct);
        });
    }
    subscribeCBValueChanged() {
        const typesCB = this.types;
        const exchangesCB = this.exchanges;
        const boundFillTree = this.fillTree.bind(this);
        this.typesCBValueChangeHandler = typesCB.on(MultiComboBoxEvents.ValueChange, boundFillTree);
        this.exchangesCBValueChangeHandler = exchangesCB.on(MultiComboBoxEvents.ValueChange, boundFillTree);
    }
    unsubscribeCBValueChanged() {
        if (!isNullOrUndefined(this.typesCBValueChangeHandler)) {
            this.typesCBValueChangeHandler.cancel();
            this.typesCBValueChangeHandler = null;
        }
        if (!isNullOrUndefined(this.exchangesCBValueChangeHandler)) {
            this.exchangesCBValueChangeHandler.cancel();
            this.exchangesCBValueChangeHandler = null;
        }
    }
    fillTree() {
        this.unsubscribeCBValueChanged();
        const filters = this.getFilters();
        const allInstruments = this.getAllInstruments();
        const visibleInstruments = this.getVisibleInstruments(allInstruments, filters);
        this.items = allInstruments;
        this.buildTree(visibleInstruments);
        const { visibleExchanges, visibleInstrumentTypes } = this.getVisibleFilters(visibleInstruments);
        this.populateExchangesComboBox(visibleExchanges);
        this.populateTypesComboBox(visibleInstrumentTypes);
        this.subscribeCBValueChanged();
        this.arrangeControls();
    }
    getVisibleFilters(visibleInstruments) {
        const visibleInstrumentTypes = [];
        const visibleExchanges = [];
        for (const element of visibleInstruments) {
            const instrument = element;
            const instrumentType = instrument.CFD ? InstrumentTypes.EQUITIES_CFD : instrument.InstrType;
            if (!visibleExchanges.Contains(instrument.TradingExchange)) {
                visibleExchanges.push(instrument.TradingExchange);
            }
            if (!visibleInstrumentTypes.Contains(instrumentType) &&
                !(InstrumentTypes.FUTURES === instrumentType && isFuturesInsteadSpot())) {
                visibleInstrumentTypes.push(instrumentType);
            }
        }
        return { visibleExchanges, visibleInstrumentTypes };
    }
    buildTree(visibleInstruments) {
        const properties = new InstrumentLookupProperties(InstrumentLookupType.Big, visibleInstruments, this.quickTree.quickTree, this.currentNameFilter);
        properties.isOptionMaster = this.isOptionMasterMode;
        properties.dataProvider = this.dataProvider;
        properties.selectedInstruments = this.selectedItem;
        properties.instrTypesToIgnore = this.instrTypesToIgnore;
        InstrumentLookupManager.fillTree(properties);
    }
    getVisibleInstruments(allInstruments, filters) {
        const visibleInstruments = allInstruments.filter((instrument) => {
            const instrumentType = instrument.CFD ? InstrumentTypes.EQUITIES_CFD : instrument.InstrType;
            const exchange = instrument.TradingExchange;
            return ((filters.isAllExchangesChecked || filters.filteredExchanges.Contains(exchange)) &&
                (filters.isAllTypesChecked || filters.filteredInstrumentTypes.Contains(instrumentType)));
        });
        return visibleInstruments;
    }
    getAllInstruments() {
        var _a, _b;
        return this.isOptionMasterMode
            ? InstrumentLookupComparer.sortOptionList(this.items, (_a = this.currentNameFilter) !== null && _a !== void 0 ? _a : '')
            : InstrumentLookupComparer.sortInstrumentList(this.items, {
                pattern: (_b = this.currentNameFilter) !== null && _b !== void 0 ? _b : '',
                isSortByInstrumentType: true
            });
    }
    getFilters() {
        const isAllExchangesChecked = isNullOrUndefined(this.exchanges) ? false : this.exchanges.isAllItemsChecked();
        const isAllTypesChecked = isNullOrUndefined(this.types) ? false : this.types.isAllItemsChecked();
        const filteredExchanges = this.getFilteredExchanges();
        const filteredInstrumentTypes = this.getFilteredInstrumentTypes();
        return { isAllExchangesChecked, filteredExchanges, isAllTypesChecked, filteredInstrumentTypes };
    }
    populateExchangesComboBox(exchanges) {
        if (isNullOrUndefined(this.exchanges)) {
            return;
        }
        const exchangesMap = this.exchanges.getItemsMap();
        const newItems = [];
        for (const exchangeEntry of exchangesMap) {
            const exchange = exchangeEntry[0];
            const checked = exchangeEntry[1];
            newItems.push({
                value: exchange,
                text: exchange,
                checked
            });
        }
        for (let i = 0; i < exchanges.length; i++) {
            const exchange = exchanges[i];
            if (exchangesMap.has(exchange)) {
                continue;
            }
            newItems.push({
                value: exchange,
                text: exchange,
                checked: true
            });
        }
        newItems.sort(TerceraInstrumentLookupDropDownForm.sortFilterItems);
        void this.exchanges.set('items', newItems);
    }
    populateTypesComboBox(instrumentTypes) {
        if (isNullOrUndefined(this.types)) {
            return;
        }
        const typesMap = this.types.getItemsMap();
        const newItems = [];
        for (const typeEntry of typesMap) {
            const insType = typeEntry[0];
            const checked = typeEntry[1];
            newItems.push({
                value: insType,
                text: InstrumentUtils.getInstrumentTypeStringLocalized(insType, false),
                checked
            });
        }
        for (let i = 0; i < instrumentTypes.length; i++) {
            const insType = instrumentTypes[i];
            if (typesMap.has(insType)) {
                continue;
            }
            newItems.push({
                value: insType,
                text: InstrumentUtils.getInstrumentTypeStringLocalized(insType, false),
                checked: true
            });
        }
        newItems.sort(TerceraInstrumentLookupDropDownForm.sortFilterItems);
        void this.types.set('items', newItems);
    }
    getFilteredInstrumentTypes() {
        const selectedInstrumentTypes = [];
        if (isNullOrUndefined(this.types)) {
            return selectedInstrumentTypes;
        }
        selectedInstrumentTypes.push(...this.types.getSelectedItems());
        const selectedTypesMap = this.types.getItemsMap();
        if (DataCache.NonFixedList) {
            const nonFixedListTypes = InstrumentLookupInstrumentType.getNonFixedListTypes();
            for (let i = 0; i < nonFixedListTypes.length; i++) {
                const nonFixedListType = nonFixedListTypes[i];
                if (selectedInstrumentTypes.Contains(nonFixedListType)) {
                    continue;
                }
                if (!selectedTypesMap.has(nonFixedListType)) {
                    selectedInstrumentTypes.push(nonFixedListType);
                }
            }
        }
        return selectedInstrumentTypes;
    }
    getFilteredExchanges() {
        const selectedExchanges = [];
        if (isNullOrUndefined(this.exchanges)) {
            return selectedExchanges;
        }
        selectedExchanges.push(...this.exchanges.getSelectedItems());
        const selectedExchangesMap = this.exchanges.getItemsMap();
        if (DataCache.NonFixedList) {
            const nonFixedExchanges = DataCache.TradingExchanges;
            for (let i = 0; i < nonFixedExchanges.length; i++) {
                const nonFixedExchange = nonFixedExchanges[i];
                if (selectedExchanges.Contains(nonFixedExchange)) {
                    continue;
                }
                if (!selectedExchangesMap.has(nonFixedExchange)) {
                    selectedExchanges.push(nonFixedExchange);
                }
            }
        }
        return selectedExchanges;
    }
    populateAsync() {
        clearTimeout(this.asyncPopulateInterval);
        this.asyncPopulateInterval = setTimeout(this.beforePopulate.bind(this), 300);
    }
    beforePopulate(list) {
        if (this.currentNameFilter === this.oldNameFilter) {
            if (!isNullOrUndefined(list)) {
                this.items = list;
            }
            this.fillTree();
            return;
        }
        const me = this;
        this.oldNameFilter = this.currentNameFilter;
        if (!isNullOrUndefined(this.dataProvider) && this.currentNameFilter) {
            if (this.isOptionMasterMode) {
                void this.dataProvider.getOptionsList(this.currentNameFilter, null).then(function (instruments) {
                    me.beforePopulate(instruments);
                });
            }
            else {
                void this.dataProvider
                    .getInstrumentsList(this.currentNameFilter, null, null, true)
                    .then(function (instruments) {
                    me.beforePopulate(instruments);
                });
            }
        }
        else if (!isNullOrUndefined(this.cachedItems)) {
            this.items = this.cachedItems;
            this.fillTree();
        }
    }
    themeChange() {
        super.themeChange();
        const collapseAllBtn = this.collapseAllBtn;
        if (!isNullOrUndefined(collapseAllBtn)) {
            void collapseAllBtn.set('terceraButtonStyle', 'js-AllSymbol-Icon');
        }
        const expandExchangesBtn = this.expandExchangesBtn;
        if (!isNullOrUndefined(expandExchangesBtn)) {
            void expandExchangesBtn.set('terceraButtonStyle', 'js-NodeExchanges-Icon');
        }
        const expandTypesBtn = this.expandTypesBtn;
        if (!isNullOrUndefined(expandTypesBtn)) {
            void expandTypesBtn.set('terceraButtonStyle', 'js-NodeTypes-Icon');
        }
    }
    localize() {
        super.localize();
        const collapseAllBtn = this.collapseAllBtn;
        if (!isNullOrUndefined(collapseAllBtn)) {
            void collapseAllBtn.set('tooltip', Resources.getResource('panel.TerceraSymbolLookupDropDownForm.collapceAllButton'));
        }
        const expandExchangesBtn = this.expandExchangesBtn;
        const expandExchangesTooltip = this.needShowExchanges()
            ? Resources.getResource('panel.TerceraSymbolLookupDropDownForm.expandExchangesButton')
            : Resources.getResource('panel.TerceraSymbolLookupDropDownForm.expandTypesButton'); // #85255
        if (!isNullOrUndefined(expandExchangesBtn)) {
            void expandExchangesBtn.set('tooltip', expandExchangesTooltip);
        }
        const expandTypesBtn = this.expandTypesBtn;
        const expandTypesTooltip = this.needShowExchanges()
            ? Resources.getResource('panel.TerceraSymbolLookupDropDownForm.expandTypesButton')
            : Resources.getResource('panel.TerceraSymbolLookupDropDownForm.expandGroupsButton'); // #85255
        if (!isNullOrUndefined(expandTypesBtn)) {
            void expandTypesBtn.set('tooltip', expandTypesTooltip);
        }
        const exchangesCB = this.exchanges;
        if (!isNullOrUndefined(exchangesCB)) {
            void exchangesCB.set({
                allItemsSelectedText: Resources.getResource('panel.TerceraSymbolLookupDropDownForm.exchangeComboBox.All exchanges'),
                noItemsSelectedText: Resources.getResource('panel.TerceraSymbolLookupDropDownForm.exchangeComboBox.No exchanges'),
                fewItemsSelectedPostfixText: Resources.getResource('panel.TerceraSymbolLookupDropDownForm.exchangeComboBox.exchanges')
            });
        }
        const typesCB = this.types;
        if (!isNullOrUndefined(typesCB)) {
            void typesCB.set({
                allItemsSelectedText: Resources.getResource('panel.TerceraSymbolLookupDropDownForm.typeComboBox.All types'),
                noItemsSelectedText: Resources.getResource('panel.TerceraSymbolLookupDropDownForm.typeComboBox.No types'),
                fewItemsSelectedPostfixText: Resources.getResource('panel.TerceraSymbolLookupDropDownForm.typeComboBox.types')
            });
        }
    }
    getSelectedNodesCount() {
        if (this.get('isMultiSelect')) {
            const qt = this.quickTree.quickTree;
            return Object.values(qt.selectedNodesDict).filter((node) => {
                const instrument = node.tag;
                return !isNullOrUndefined(instrument) && !(instrument.isOptionSymbol && instrument.StrikePrice <= 0);
            }).length;
        }
        else {
            return super.getSelectedNodesCount();
        }
    }
    isButtonAddEnable() {
        var _a, _b;
        const qt = this.quickTree.quickTree;
        const result = super.isButtonAddEnable();
        if (this.get('isMultiSelect')) {
            const hasNonNullInstrumentTradableID = Object.values(qt.selectedNodesDict).some((node) => {
                const instrument = node.tag;
                return !isNullOrUndefined(instrument) && !(instrument.isOptionSymbol && instrument.StrikePrice <= 0);
            });
            return result && hasNonNullInstrumentTradableID;
        }
        else {
            return result && !isNullOrUndefined((_b = (_a = qt.selectedNode) === null || _a === void 0 ? void 0 : _a.tag) === null || _b === void 0 ? void 0 : _b.InstrumentTradableID);
        }
    }
    isValidResult(nodeTag) {
        const instrument = nodeTag;
        if (isNullOrUndefined(instrument)) {
            return false;
        }
        return isValidNumber(instrument.InstrumentTradableID);
    }
    static sortFilterItems(i1, i2) {
        return i1.text.toLowerCase().localeCompare(i2.text.toLowerCase());
    }
}
var NodeType;
(function (NodeType) {
    NodeType[NodeType["Exchange"] = 0] = "Exchange";
    NodeType[NodeType["InstrumentType"] = 1] = "InstrumentType";
    NodeType[NodeType["InstrumentGroup"] = 2] = "InstrumentGroup";
    NodeType[NodeType["Instrument"] = 3] = "Instrument";
    NodeType[NodeType["Other"] = 4] = "Other";
})(NodeType || (NodeType = {}));
TerceraLookupDropDownForm.extendWith(TerceraInstrumentLookupDropDownForm, {
    partials: { bodyPartial: TerceraInstrumentLookupDropDownFormTemplate }
});
